import { FC, useCallback, useMemo } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { Label, SearchableSelect, cn } from "@racoon/ui";
import { matchesAnySearch } from "@racoon/lib";

/** Read-only value box of the design (Surface/disabled + Text/disabled), sized to sit next to a select. */
export const readonlyBox = (size: FieldSize = "sm") =>
  cn(
    "flex items-center rounded-lg bg-slate-200 px-2 text-sm leading-4 tracking-[-0.01em] text-slate-400 whitespace-nowrap",
    size === "sm" ? "h-8" : "h-10",
  );

/** `sm` (32px) is the current design; `md` (40px) matches the older forms' controls. */
export type FieldSize = "sm" | "md";

/** A form row label (Subhead Medium). */
// `!leading-4`: the Label base sets `leading-none`, and `cn` does not merge.
export const FIELD_LABEL = "text-sm font-medium !leading-4 tracking-[-0.2px] text-text-body";

type Organization = { id: bigint | string; name: string; bin?: string };

type Props = {
  /** Select id; the BIN box gets `${id}-bin`. */
  id: string;
  organizations: Organization[];
  value: string;
  onChange: (id: string) => void;
  /** BIN to show when the selected org is not in `organizations` yet (edit mode while loading). */
  fallbackBin?: string;
  valueLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  error?: string;
  size?: FieldSize;
};

/** Customer picker plus its read-only BIN; the search matches the name or the BIN. */
export const CustomerBinRow: FC<Props> = ({
  id,
  organizations,
  value,
  onChange,
  fallbackBin,
  valueLabel,
  loading,
  disabled,
  invalid,
  error,
  size = "sm",
}) => {
  const t = useT();
  const binById = useMemo(
    () => new Map(organizations.map((org) => [String(org.id), org.bin?.trim() ?? ""])),
    [organizations],
  );
  const options = useMemo(
    () => organizations.map((org) => ({ id: String(org.id), name: org.name })),
    [organizations],
  );
  const filterOption = useCallback(
    (option: { id: string; name: string }, query: string) =>
      matchesAnySearch([option.name, binById.get(option.id)], query),
    [binById],
  );
  const bin = (value && binById.get(value)) || fallbackBin || "";

  return (
    <div className="flex items-start gap-16">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <SearchableSelect
          id={id}
          size={size}
          value={value}
          options={options}
          onChange={onChange}
          filterOption={filterOption}
          valueLabel={valueLabel}
          loading={loading}
          disabled={disabled}
          invalid={invalid}
          placeholder={t(Namespace.PickupLocation, "modal.selectCustomer")}
          searchPlaceholder={t(Namespace.PickupLocation, "modal.searchCustomer")}
        />
        {error && <span className="text-xs text-text-error">{error}</span>}
      </div>
      <div className="flex w-[267px] shrink-0 items-center gap-4">
        <Label htmlFor={`${id}-bin`} className={cn(FIELD_LABEL, "whitespace-nowrap")}>
          {t(Namespace.PickupLocation, "modal.customerBin")}
        </Label>
        <output
          id={`${id}-bin`}
          className={cn(readonlyBox(size), "min-w-0 flex-1 overflow-hidden")}
        >
          {bin}
        </output>
      </div>
    </div>
  );
};
